<template>
  <div>
    <h3 class="pt-3">{{ title }}</h3>
    <p>{{ introductionText }}</p>
    <b-card no-body class="mt-3">
      <b-tabs v-if="!qasCreated" ref="tabs" v-model="currentPage" card>
        <b-tab
          ref="info"
          :title="$t('qas.information')"
          :disabled="qasCreated || uploading"
        >
          <p>{{ instructionText }}</p>
          <hr />
          <b-form no-validate>
            <b-row>
              <b-col cols="12">
                <b-form-group>
                  {{ $t("qas.fields.anonymous") }}
                  <b-form-checkbox
                    v-model="qas.anonymous"
                    class="mb-auto"
                    :value="true"
                    :unchecked-value="false"
                    button
                    size="sm"
                    :button-variant="qas.anonymous ? 'danger' : 'secondary'"
                  >
                    {{ $t("labels.no") }}
                  </b-form-checkbox>
                  <b-form-checkbox
                    v-model="qas.anonymous"
                    class="mb-auto ml-1"
                    :value="false"
                    :unchecked-value="true"
                    button
                    size="sm"
                    :button-variant="qas.anonymous ? 'secondary' : 'info'"
                  >
                    {{ $t("labels.yes") }}
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-show="!qas.anonymous">
              <b-col cols="12" lg="6">
                <b-form-group :label="i18n.fullName">
                  <b-form-input
                    v-model="qas.person.fullName"
                    v-validate="rules.person.fullName"
                    type="text"
                    class="form-control"
                    aria-describedby="invalidFullName"
                    name="fullName"
                    :state="validateState('fullName')"
                  />
                  <b-form-invalid-feedback id="invalidFullName">
                    {{ errors.first("fullName") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col cols="12" lg="3">
                <b-form-group :label="i18n.cpf">
                  <b-form-input
                    ref="cpf"
                    v-model="qas.person.cpf"
                    v-mask="'###.###.###-##'"
                    v-validate="rules.person.cpf"
                    type="text"
                    class="form-control"
                    aria-describedby="invalidCPF"
                    name="cpf"
                    :state="validateState('cpf')"
                  />
                  <b-form-invalid-feedback id="invalidCPF">
                    {{ errors.first("cpf") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col cols="12" lg="3">
                <b-form-group :label="i18n.birthday">
                  <b-form-input
                    v-model="qas.person.birthday"
                    v-validate="rules.person.birthday"
                    type="date"
                    class="form-control"
                    aria-describedby="invalidBirthday"
                    name="birthday"
                    :state="validateState('birthday')"
                  />
                  <b-form-invalid-feedback id="invalidBirthday">
                    {{ errors.first("birthday") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-show="!qas.anonymous">
              <b-col cols="12" lg="6">
                <b-form-group :label="i18n.email">
                  <b-form-input
                    v-model="qas.person.email"
                    v-validate="rules.person.email"
                    type="email"
                    class="form-control"
                    aria-describedby="invalidEmail"
                    name="email"
                    :state="validateState('email')"
                  />
                  <b-form-invalid-feedback id="invalidEmail">
                    {{ errors.first("email") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col cols="12" lg="3">
                <b-form-group :label="i18n.phone">
                  <b-form-input
                    ref="phone"
                    v-model="qas.person.phone"
                    v-mask="['(##) ####-####', '(##) #####-####']"
                    v-validate="rules.person.phone"
                    type="text"
                    class="form-control"
                    aria-describedby="invalidPhone"
                    name="phone"
                    :state="validateState('phone')"
                  />
                  <b-form-invalid-feedback id="invalidPhone">
                    {{ errors.first("phone") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col col xs="12" lg="3" md="6">
                <b-form-group
                  :label="$t('qas.fields.relation', [company.fantasyName])"
                >
                  <b-form-input
                    v-model="qas.relation"
                    v-validate="rules.relation"
                    class="form-control"
                    aria-describedby="invalidRelation"
                    name="qasRelation"
                    :state="validateState('qasRelation')"
                  />
                  <b-form-invalid-feedback id="invalidRelation">
                    {{ errors.first("qasRelation") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              <b-col cols="12">
                <b-form-group
                  :label="$t('qas.fields.text')"
                  :description="$t('qas.fieldsDesc.text')"
                >
                  <b-form-textarea
                    v-model="qas.text"
                    v-validate="rules.text"
                    class="form-control"
                    aria-describedby="invalidText"
                    name="qasText"
                    :rows="4"
                    :state="validateState('qasText')"
                  />
                  <b-form-invalid-feedback id="invalidText">
                    {{ errors.first("qasText") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
          </b-form>
        </b-tab>
        <b-tab
          :title="$t('qas.attachments')"
          :disabled="(!infoValidated || qasCreated) && !uploading"
        >
          <h5 class="pt-1 pb-1">{{ $t("qas.attachmentInfo") }}</h5>
          <multiple-file-upload
            ref="upload"
            :post-action="upload.postAction"
            :data="upload.data"
            :disabled="uploading || uploadComplete"
            @upload-complete="onUploadComplete"
          />
        </b-tab>
      </b-tabs>
      <b-card-body v-else-if="qasCreated">
        <h3>{{ $t("qas.success") }}</h3>
        <h5>{{ $t("qas.successInfo1") }}<br />{{ $t("qas.successInfo2") }}</h5>
        <h5>
          {{ $t("labels.protocol") }}: <strong>{{ context.protocol }}</strong>
        </h5>
      </b-card-body>
      <template slot="footer">
        <template v-if="qasCreated">
          <b-button
            variant="primary"
            class="mr-1"
            :disabled="loading"
            @click="$router.replace('/')"
            ><i class="fas fa-home" aria-hidden="true" />
            {{ $t("labels.home") }}
          </b-button>
        </template>
        <template v-else>
          <b-button class="mr-1" :disabled="loading" @click="previousPage">{{
            $t("labels.back")
          }}</b-button>
          <b-button
            variant="primary"
            :disabled="
              loading || (currentPage === 1 && $refs.upload.hasInvalidFiles)
            "
            @click="nextPage"
          >
            {{ currentPage === 1 ? $t("labels.finish") : $t("labels.next") }}
          </b-button>
        </template>
      </template>
    </b-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { ValidatorDictionary } from "@/i18n";
import { mask } from "vue-the-mask";
import MultipleFileUpload from "../../../components/MultipleFileUpload";
import { sendQaS } from "@/api/main";

export default {
  name: "CompanyQaS",
  directives: { mask },
  components: { MultipleFileUpload },
  data() {
    return {
      qas: {
        anonymous: true,
        person: {},
      },
      currentPage: 0,
      totalPages: 2,
      infoValidated: false,
      loading: false,
      uploading: false,
      uploadComplete: false,
      upload: {
        postAction: "",
        data: {},
      },
      context: {
        protocol: null,
      },
      qasCreated: false,
    };
  },
  computed: {
    ...mapGetters(["company", "portal"]),
    title() {
      const portal = this.portal;
      if (portal && portal.qas && portal.qas.title) return portal.qas.title;
      return this.$i18n.t("qas.title");
    },
    introductionText() {
      const portal = this.portal;
      if (portal && portal.qas && portal.qas.introductionText)
        return portal.qas.introductionText;
      return this.$i18n.t("qas.introduction");
    },
    instructionText() {
      const portal = this.portal;
      if (portal && portal.qas && portal.qas.instructionText)
        return portal.qas.instructionText;
      return this.$i18n.t("qas.instruction");
    },
    rules() {
      return {
        person: {
          fullName: { required: !this.qas.anonymous },
          cpf: { required: false, cpf: true },
          birthday: { required: false },
          email: { required: false, email: true },
          phone: { required: false },
        },
        text: { required: true },
        relation: { required: false },
        termsAccepted: { required: true, acceptance: true },
      };
    },

    i18n() {
      return ValidatorDictionary[this.$i18n.locale].attributes;
    },
  },

  created() {
    const updateEls = ["cpf", "phone"];
    this.$nextTick(() => {
      for (const id of updateEls) {
        this.$refs[id].$el.oninput({ isTrusted: true });
      }
      this.$validator.reset();
    });
  },
  methods: {
    sanitizePayload(payload) {
      if (payload.person.phone) {
        payload.person.phone = payload.person.phone.replace(/[^\d]/g, "");
        if (!payload.person.phone) delete payload.person.phone;
      }
      if (payload.person.cpf) {
        payload.person.cpf = payload.person.cpf.replace(/[^\d]/g, "");
        if (!payload.person.cpf) delete payload.person.cpf;
      }
    },
    async submitQaS() {
      this.loading = true;
      try {
        const payload = JSON.parse(JSON.stringify(this.qas));
        this.sanitizePayload(payload);

        const response = await sendQaS(this.portal.slug, payload);
        if (response.status === 201) {
          const { protocol } = response.data;
          const files = this.$refs.upload.selectedFiles();
          this.context = {
            protocol,
          };
          if (files.length > 0) {
            this.uploading = true;
            this.currentPage = 1;

            this.upload.postAction = `/api/qas/${this.portal.slug}/${protocol}/files`;
            for (const file of files) {
              file.postAction = this.upload.postAction;
              file.headers = this.upload.headers;
            }

            this.$nextTick(() => this.$refs.upload.start());
          } else {
            this.onQaSCreated(protocol);
          }
        }
      } catch (error) {
        this.loading = false;
        if (
          error.response.status === 403 ||
          error.response.status === 400 ||
          error.response.status === 404
        ) {
          this.$bvToast.toast(error.response.data, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        } else {
          this.$bvToast.toast(error.response.statusText, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        }
      }
    },

    async onUploadComplete() {
      this.uploadComplete = true;
      this.uploading = false;
      this.$nextTick(() => {
        this.currentPage = 2;
        this.$nextTick(() => {
          this.onQaSCreated(this.context.protocol);
        });
      });
    },

    onQaSCreated() {
      this.$bvToast.toast(this.$i18n.t("qas.success"), {
        title: "Sucesso",
        toastClass: "custom-toast custom-toast-success",
        variant: "light",
        solid: true,
        autoHideDelay: 5000,
        noCloseButton: false,
      });
      this.loading = false;
      this.qasCreated = true;
    },
    async nextPage() {
      const next = () => {
        this.currentPage++;
        if (this.currentPage >= this.totalPages) {
          this.currentPage = 0;
        }
      };

      if (this.currentPage === 0) {
        let valid = await this.$validator.validateAll();
        valid =
          valid ||
          (this.errors.items.length === 1 &&
            this.errors.items[0].field === "termsAccepted");
        this.infoValidated = valid;
        if (valid) {
          this.$nextTick(next);
        }
      } else if (this.currentPage === 1) {
        if (!this.$refs.upload.hasInvalidFiles) {
          if (await this.$validator.validateAll()) {
            await this.submitQaS();
          }
        }
      } else {
        next();
      }
    },
    previousPage() {
      this.currentPage--;
      if (this.currentPage < 0) {
        this.currentPage = this.totalPages - 1;
      }
    },
    validateState(ref) {
      if (
        this.vFields[ref] &&
        (this.vFields[ref].dirty || this.vFields[ref].validated)
      ) {
        return !this.errors.has(ref);
      }
      return null;
    },

    goBack() {
      this.$router.go(-1);
    },
  },
};
</script>
